import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listLeaveTypes, getLeaveBalances, requestLeave } from '../lib/db.js';
import { useAuth } from '../lib/auth.jsx';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';

const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);

export function LeaveRequest() {
  const { employeeId } = useAuth();
  const nav = useNavigate();
  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    listLeaveTypes().then((t) => { setTypes(t); setTypeId(t[0]?.id || ''); }).catch((e) => setErr(e.message));
    if (employeeId) getLeaveBalances(employeeId).then(setBalances).catch(() => {});
  }, [employeeId]);

  const type = types.find((t) => t.id === typeId);
  const bal = balances.find((b) => b.code === type?.code);
  const days = useMemo(() => (end >= start ? daysBetween(start, end) : 1), [start, end]);
  const needCert = type?.code === 'sick' && days >= 3;

  async function onSubmit(e) {
    e.preventDefault();
    if (!employeeId) { setErr('บัญชีนี้ยังไม่ผูกกับข้อมูลพนักงาน'); return; }
    if (end < start) { setErr('วันสิ้นสุดต้องไม่ก่อนวันเริ่ม'); return; }
    setErr(''); setBusy(true);
    try {
      await requestLeave({
        employee_id: employeeId, leave_type_id: typeId, code: type?.code, name_th: type?.name_th,
        start_date: start, end_date: end, days, reason: reason.trim(),
      });
      setDone(true);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  }

  if (done) {
    return (
      <Shell>
        <div className="ok-banner" style={{ marginTop: 30 }}>
          <div className="l1">✓ ส่งคำขอลาแล้ว</div>
          <div className="note">{type?.name_th} · {days} วัน · รอหัวหน้าอนุมัติ</div>
        </div>
        <Link to="/me"><button className="cta ghost" style={{ marginTop: 18 }}>กลับหน้าของฉัน</button></Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={onSubmit}>
        <label className="f">ประเภทการลา</label>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name_th}</option>)}
        </select>
        {bal && (
          <div className="leave-note">สิทธิคงเหลือปีนี้: <b>{bal.remaining} วัน</b> (ใช้ไป {bal.used}/{bal.entitled})</div>
        )}
        {type?.notes || type?.note ? <p className="note" style={{ marginTop: 8 }}>{type.notes || type.note}</p> : null}

        <label className="f">วันที่เริ่ม</label>
        <input type="date" value={start} onChange={(e) => { setStart(e.target.value); if (e.target.value > end) setEnd(e.target.value); }} />
        <label className="f">วันที่สิ้นสุด</label>
        <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />

        <div className="leave-note" style={{ background: 'var(--surface-2)' }}>รวม <b>{days} วัน</b></div>
        {needCert && <p className="note" style={{ marginTop: 8, color: 'var(--warn)' }}>⚠︎ ลาป่วย ≥3 วัน ต้องแนบใบรับรองแพทย์</p>}

        <label className="f">เหตุผล</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ระบุเหตุผลการลา…" />

        {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
        <button className="cta" type="submit" disabled={busy}>{busy ? 'กำลังส่ง…' : 'ส่งคำขอลา'}</button>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  const nav = useNavigate();
  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        <div className="title-row">
          <div><div className="eyebrow">พนักงาน</div><h1 className="page">ขอลา</h1></div>
          <span className="glyph">🗓</span>
        </div>
        {children}
      </section>
      <BottomNav />
    </div>
  );
}
